import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications, useUI } from '../hooks';
import Text from '../components/Text/Text';
import { Notification } from '../types';

const NotificationsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { notifications, getNotifications, markAsRead, isLoading, error } = useNotifications();
  const { showToast } = useUI();
  
  // Use a ref to track if notifications have been fetched
  const hasLoadedNotifications = useRef(false);
  
  // Fetch notifications only once on component mount
  // We intentionally omit dependencies to prevent infinite loops
  // The ref ensures we only fetch once regardless of re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Only fetch if we haven't already loaded notifications
    if (!hasLoadedNotifications.current) {
      const fetchNotifications = async () => {
        try {
          await getNotifications();
          // Mark as loaded
          hasLoadedNotifications.current = true;
        } catch (error) {
          showToast('error', 'Failed to load notifications');
        }
      };
      
      fetchNotifications();
    }
  }, []); // Empty dependency array ensures this only runs once on mount
  
  // Handle notification click
  const handleNotificationClick = async (notification: Notification) => {
    try {
      // Mark notification as read
      if (!notification.read) {
        await markAsRead(notification.id);
      }
      
      // Navigate based on notification type
      if (notification.relatedType === 'transaction' && notification.relatedId) {
        navigate(`/payment/${notification.relatedId}`);
      } else if (notification.relatedType === 'tracking' && notification.relatedId) {
        navigate(`/tracking/${notification.relatedId}`);
      }
    } catch (error) {
      showToast('error', 'Failed to process notification');
    }
  };
  
  // Render notification item
  const renderNotificationItem = (notification: Notification) => {
    // Determine icon based on notification type
    let icon;
    if (notification.type === 'funds_added') {
      icon = (
        <div className="notification-icon funds-added">
          <svg viewBox="0 0 24 24" width="24" height="24" stroke="#8a2be2" fill="none">
            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      );
    } else if (notification.type === 'tracking_added') {
      icon = (
        <div className="notification-icon tracking-added">
          <svg viewBox="0 0 24 24" width="24" height="24" stroke="#4169e1" fill="none">
            <path d="M22 12h-4l-3 9L9 3l-3 9H2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      );
    } else if (notification.type === 'funds_released') {
      icon = (
        <div className="notification-icon funds-released">
          <svg viewBox="0 0 24 24" width="24" height="24" stroke="#2e8b57" fill="none">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M22 4L12 14.01l-3-3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      );
    } else if (notification.type === 'status_update') {
      icon = (
        <div className="notification-icon status-update">
          <svg viewBox="0 0 24 24" width="24" height="24" stroke="#ff8c00" fill="none">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      );
    } else {
      icon = (
        <div className="notification-icon system">
          <svg viewBox="0 0 24 24" width="24" height="24" stroke="#6c757d" fill="none">
            <circle cx="12" cy="12" r="10" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <line x1="12" y1="16" x2="12" y2="12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <line x1="12" y1="8" x2="12.01" y2="8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      );
    }
    
    return (
      <div 
        key={notification.id} 
        className={`notification-item ${notification.read ? 'read' : 'unread'}`}
        onClick={() => handleNotificationClick(notification)}
      >
        {icon}
        <div className="notification-content">
          <Text className="text-body">{notification.title}</Text>
          <Text className="text-caption">{notification.message}</Text>
          <Text className="text-caption text-gray">
            {new Date(notification.createdAt).toLocaleString()}
          </Text>
        </div>
      </div>
    );
  };
  
  return (
    <div className="notifications-container">
      <div className="notifications-list">
        {isLoading ? (
          <div className="loading-state">
            <Text>Loading notifications...</Text>
          </div>
        ) : error ? (
          <div className="error-state">
            <Text className="text-error">Failed to load notifications</Text>
            <button 
              className="retry-button"
              onClick={() => getNotifications()}
            >
              Retry
            </button>
          </div>
        ) : notifications.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <svg viewBox="0 0 24 24" width="48" height="48" stroke="#6c757d" fill="none">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <Text className="text-body">No notifications yet</Text>
            <Text className="text-caption text-gray">
              When you receive notifications, they will appear here
            </Text>
          </div>
        ) : (
          notifications.map(renderNotificationItem)
        )}
      </div>
    </div>
  );
};

export default NotificationsScreen;